import { useEffect, useMemo, useRef } from 'react';
import { di } from 'react-magnetic-di';
import type { AttachmentConfiguration } from '@atlassian/proforma-form-renderer/attachment';

import { useMediaUploadConfig } from '../../../../controllers/request-create-context-provider';

/**
 * Creates the attachment configuration for the FormRenderer.
 *
 * For request creation, we need both read and upload configurations:
 * - Upload config: for uploading new attachments
 * - Read config: for displaying thumbnails of newly uploaded files in the filmstrip
 *
 * Both configs use the same credentials since newly uploaded files reside in the same
 * media collection.
 *
 * Returns undefined if media config is not available (graceful degradation),
 * which causes FormRenderer to disable attachment features.
 *
 * Token refresh pattern: We store tokens in refs so they can be refreshed and
 * returned from the AuthProvider. Media components cache the AuthProvider, so
 * if we build a new AuthProvider which returns a new token, the Media components
 * will not use the updated token.
 */
export const useAttachmentConfiguration = (): AttachmentConfiguration | undefined => {
	di(useMediaUploadConfig);

	const { baseUrl, collection, clientId, token } = useMediaUploadConfig();

	// Store token in a ref so it can be refreshed and returned from the AuthProvider.
	// Media components cache the AuthProvider, so if we build a new AuthProvider which
	// returns a new token, the Media components will not use the updated token.
	const tokenRef = useRef<string>(token);

	useEffect(() => {
		tokenRef.current = token;
	}, [token]);

	const attachmentConfiguration: AttachmentConfiguration | undefined = useMemo(() => {
		// Return undefined if media config is not available (graceful degradation)
		if (!baseUrl || !clientId) {
			return undefined;
		}

		return {
			// Read config is needed to display thumbnails of newly uploaded files.
			// Uses the same credentials as upload since files are in the same collection.
			read: {
				mediaClientConfig: {
					authProvider: () =>
						Promise.resolve({
							baseUrl,
							clientId,
							token: tokenRef.current,
						}),
				},
			},
			upload: {
				mediaClientConfig: {
					authProvider: () =>
						Promise.resolve({
							baseUrl,
							clientId,
							token: tokenRef.current,
						}),
				},
				mediaBrowserConfig: {
					multiple: true,
					uploadParams: {
						collection,
					},
				},
				mediaDropzoneConfig: {
					uploadParams: {
						collection,
					},
				},
			},
		};
	}, [baseUrl, clientId, collection]);

	return attachmentConfiguration;
};
